import { useEffect, useMemo, useState } from "react";
import api, { useList, fmtRM, apiFileUrl, MONTH_NOW } from "@/lib/api";
import { PageHeader, StatusBadge, Spinner, EmptyState } from "@/components/common";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { FileDown, FileText, RefreshCcw } from "lucide-react";

// ---------------------- Filter bar (month + from/to + export buttons) ----------------------
function ReportFilters({ month, setMonth, fromDate, setFromDate, toDate, setToDate,
                         onExport, isLoading, csvOnly = false, pdfOnly = false }) {
  return (
    <div className="flex flex-wrap items-end gap-3 mb-5 rounded-lg border bg-card p-4" data-testid="report-filters">
      <div className="space-y-1">
        <Label className="text-xs">Month</Label>
        <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)}
               className="w-40" data-testid="filter-month" />
      </div>
      <div className="text-xs text-muted-foreground pb-2">or</div>
      <div className="space-y-1">
        <Label className="text-xs">From date</Label>
        <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)}
               className="w-44" data-testid="filter-from" />
      </div>
      <div className="space-y-1">
        <Label className="text-xs">To date</Label>
        <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)}
               className="w-44" data-testid="filter-to" />
      </div>
      <div className="flex-1 min-w-[20px]"></div>
      {!pdfOnly && (
        <Button variant="outline" size="sm" onClick={() => onExport("csv")} disabled={isLoading}
                data-testid="export-csv">
          <FileDown className="h-4 w-4 mr-1" />CSV
        </Button>
      )}
      {!csvOnly && (
        <Button variant="outline" size="sm" onClick={() => onExport("pdf")} disabled={isLoading}
                data-testid="export-pdf">
          <FileText className="h-4 w-4 mr-1" />PDF
        </Button>
      )}
      <Button variant="ghost" size="sm" onClick={() => onExport("refresh")} disabled={isLoading}
              data-testid="refresh-report">
        <RefreshCcw className="h-4 w-4" />
      </Button>
    </div>
  );
}

// Pull report JSON or trigger CSV/PDF download
function useReport(endpoint, params) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const search = useMemo(() => {
    const s = new URLSearchParams();
    Object.entries(params || {}).forEach(([k, v]) => { if (v) s.append(k, v); });
    return s.toString();
  }, [params]);

  const reload = async () => {
    setLoading(true);
    try {
      const r = await api.get(`${endpoint}?${search}`);
      setData(r.data);
    } finally { setLoading(false); }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await api.get(`${endpoint}?${search}`);
        if (!cancelled) setData(r.data);
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const download = (format) => {
    const url = `${apiFileUrl(endpoint)}?${search}&format=${format}`;
    window.open(url, "_blank");
  };

  return { data, loading, reload, download };
}

// ---------------------- 1) Summary (existing) ----------------------
function SummaryTab() {
  const { data, loading } = useList("/reports/summary");
  if (loading || !data) return <Spinner />;
  return (
    <div data-testid="summary-tab">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="rounded-lg border bg-card p-5">
          <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Collected this month</p>
          <p className="text-2xl font-bold font-display mt-1" data-testid="report-collected">{fmtRM(data.collected_total)}</p>
        </div>
        <div className="rounded-lg border bg-card p-5">
          <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Outstanding</p>
          <p className="text-2xl font-bold font-display mt-1 text-[#D90429]">{fmtRM(data.outstanding_total)}</p>
        </div>
        <div className="rounded-lg border bg-card p-5">
          <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Payroll cost this month</p>
          <p className="text-2xl font-bold font-display mt-1">{fmtRM(data.payroll_cost_this_month)}</p>
        </div>
      </div>
      <div className="rounded-lg border bg-card p-6">
        <h3 className="text-lg font-semibold mb-4">Revenue collected by month</h3>
        {data.revenue_by_month.length === 0 ? <EmptyState text="No payments recorded yet." /> : (
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={data.revenue_by_month}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E3DB" />
              <XAxis dataKey="month" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip formatter={(v) => fmtRM(v)} />
              <Bar dataKey="amount" fill="#1A4331" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}

// ---------------------- Generic filter+table helpers ----------------------
function useFilterState() {
  const [month, setMonth] = useState(MONTH_NOW);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  // when from/to set, clear month (and vice versa)
  const params = useMemo(() => {
    if (fromDate || toDate) return { from_date: fromDate, to_date: toDate };
    return { month };
  }, [month, fromDate, toDate]);
  return { month, setMonth, fromDate, setFromDate, toDate, setToDate, params };
}

// ---------------------- 2) Collection ----------------------
function CollectionTab() {
  const f = useFilterState();
  const { data, loading, reload, download } = useReport("/admin/reports/collection", f.params);
  const handleExport = (fmt) => fmt === "refresh" ? reload() : download(fmt);
  return (
    <div data-testid="collection-tab">
      <ReportFilters {...f} onExport={handleExport} isLoading={loading} />
      {loading || !data ? <Spinner /> : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-4">
            <div className="rounded-lg border bg-card p-4">
              <p className="text-xs uppercase text-muted-foreground">Total collected</p>
              <p className="text-xl font-bold mt-1" data-testid="collection-total">{fmtRM(data.total)}</p>
            </div>
            <div className="rounded-lg border bg-card p-4">
              <p className="text-xs uppercase text-muted-foreground">Transactions</p>
              <p className="text-xl font-bold mt-1">{data.count}</p>
            </div>
            <div className="rounded-lg border bg-card p-4 md:col-span-2">
              <p className="text-xs uppercase text-muted-foreground mb-1">By payment method</p>
              <div className="flex flex-wrap gap-x-3 gap-y-1">
                {Object.entries(data.by_method).map(([k, v]) => (
                  <span key={k} className="text-sm" data-testid={`by-method-${k}`}>
                    <span className="text-muted-foreground capitalize">{k.replace("_", " ")}:</span>{" "}
                    <span className="font-semibold">{fmtRM(v)}</span>
                  </span>
                ))}
              </div>
            </div>
          </div>
          <DataTable rows={data.rows} loading={false} columns={[
            { key: "date", label: "Date" }, { key: "invoice_no", label: "Invoice #" },
            { key: "student", label: "Student" }, { key: "method", label: "Method" },
            { key: "reference", label: "Reference" },
            { key: "amount", label: "Amount", align: "right", render: (v) => fmtRM(v) },
          ]} testId="collection-table" />
        </>
      )}
    </div>
  );
}

// ---------------------- 3) SST ----------------------
function SSTTab() {
  const f = useFilterState();
  const { data, loading, reload, download } = useReport("/admin/reports/sst", f.params);
  const handleExport = (fmt) => fmt === "refresh" ? reload() : download(fmt);
  return (
    <div data-testid="sst-tab">
      <ReportFilters {...f} onExport={handleExport} isLoading={loading} />
      {loading || !data ? <Spinner /> : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
            <div className="rounded-lg border bg-card p-4">
              <p className="text-xs uppercase text-muted-foreground">Total SST collected</p>
              <p className="text-xl font-bold mt-1" data-testid="sst-total">{fmtRM(data.total_sst)}</p>
            </div>
            <div className="rounded-lg border bg-card p-4">
              <p className="text-xs uppercase text-muted-foreground">Paid invoices</p>
              <p className="text-xl font-bold mt-1">{data.count}</p>
            </div>
            <div className="rounded-lg border bg-card p-4">
              <p className="text-xs uppercase text-muted-foreground">SST rate</p>
              <p className="text-xl font-bold mt-1">8%</p>
            </div>
          </div>
          <DataTable rows={data.rows} loading={false} columns={[
            { key: "invoice_month", label: "Period" }, { key: "invoice_no", label: "Invoice #" },
            { key: "student", label: "Student" },
            { key: "subtotal", label: "Subtotal", align: "right", render: (v) => fmtRM(v) },
            { key: "sst_amount", label: "SST", align: "right", render: (v) => fmtRM(v) },
            { key: "total", label: "Total", align: "right", render: (v) => fmtRM(v) },
          ]} testId="sst-table" />
        </>
      )}
    </div>
  );
}

// ---------------------- 4) Deposit Liability ----------------------
function DepositTab() {
  const [asOf, setAsOf] = useState(new Date().toISOString().slice(0, 10));
  const { data, loading, reload, download } = useReport("/admin/reports/deposit-liability", { as_of: asOf });
  return (
    <div data-testid="deposit-tab">
      <div className="flex flex-wrap items-end gap-3 mb-5 rounded-lg border bg-card p-4">
        <div className="space-y-1">
          <Label className="text-xs">As of date</Label>
          <Input type="date" value={asOf} onChange={(e) => setAsOf(e.target.value)}
                 className="w-44" data-testid="filter-as-of" />
        </div>
        <div className="flex-1"></div>
        <Button variant="outline" size="sm" onClick={() => download("csv")} disabled={loading} data-testid="export-csv">
          <FileDown className="h-4 w-4 mr-1" />CSV
        </Button>
        <Button variant="outline" size="sm" onClick={() => download("pdf")} disabled={loading} data-testid="export-pdf">
          <FileText className="h-4 w-4 mr-1" />PDF
        </Button>
        <Button variant="ghost" size="sm" onClick={reload} disabled={loading} data-testid="refresh-report">
          <RefreshCcw className="h-4 w-4" />
        </Button>
      </div>
      {loading || !data ? <Spinner /> : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
            <div className="rounded-lg border bg-card p-4">
              <p className="text-xs uppercase text-muted-foreground">Total liability held</p>
              <p className="text-xl font-bold mt-1 text-amber-700" data-testid="deposit-total">{fmtRM(data.total_balance)}</p>
            </div>
            <div className="rounded-lg border bg-card p-4">
              <p className="text-xs uppercase text-muted-foreground">Parents with deposits</p>
              <p className="text-xl font-bold mt-1">{data.parent_count}</p>
            </div>
            <div className="rounded-lg border bg-card p-4">
              <p className="text-xs uppercase text-muted-foreground">Deposit entries</p>
              <p className="text-xl font-bold mt-1">{data.deposit_count}</p>
            </div>
          </div>
          <DataTable rows={data.rows} loading={false} columns={[
            { key: "collected_date", label: "Collected" }, { key: "parent", label: "Parent" },
            { key: "student", label: "Student" }, { key: "course", label: "Course" },
            { key: "invoice_no", label: "Invoice #" },
            { key: "balance", label: "Balance", align: "right", render: (v) => fmtRM(v) },
            { key: "status", label: "Status", render: (v) => <StatusBadge status={v} /> },
          ]} testId="deposit-table" />
        </>
      )}
    </div>
  );
}

// ---------------------- 5) Student Movement ----------------------
function MovementTab() {
  const f = useFilterState();
  const { data, loading, reload, download } = useReport("/admin/reports/student-movement", f.params);
  const handleExport = (fmt) => fmt === "refresh" ? reload() : download(fmt);
  return (
    <div data-testid="movement-tab">
      <ReportFilters {...f} onExport={handleExport} isLoading={loading} />
      {loading || !data ? <Spinner /> : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <StatCard label="New enrolments" value={data.new_enrolments} accent="emerald" testId="movement-new" />
            <StatCard label="Drop-outs" value={data.dropouts} accent="rose" testId="movement-drops" />
            <StatCard label="On-hold" value={data.on_hold} accent="amber" />
            <StatCard label="Reactivations" value={data.reactivations} />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
            <div className="rounded-lg border bg-card p-4">
              <p className="text-xs uppercase text-muted-foreground">Currently active</p>
              <p className="text-xl font-bold mt-1">{data.active_count}</p>
            </div>
            <div className="rounded-lg border bg-card p-4">
              <p className="text-xs uppercase text-muted-foreground">Waitlist (active)</p>
              <p className="text-xl font-bold mt-1">{data.projection.waitlist_active}</p>
            </div>
            <div className="rounded-lg border bg-card p-4 bg-secondary/5">
              <p className="text-xs uppercase text-secondary">Projected next month</p>
              <p className="text-xl font-bold mt-1 text-secondary" data-testid="movement-projected">{data.projection.projected_next_month}</p>
            </div>
          </div>
          <DataTable rows={data.rows} loading={false} columns={[
            { key: "date", label: "Date" }, { key: "type", label: "Movement",
              render: (v) => <StatusBadge status={v.toLowerCase().replace(/[\s-]/g, "_")} /> },
            { key: "student", label: "Student" }, { key: "detail", label: "Detail" },
          ]} testId="movement-table" />
        </>
      )}
    </div>
  );
}

// ---------------------- 6) Drop-out ----------------------
function DropoutTab() {
  const f = useFilterState();
  const { data, loading, reload, download } = useReport("/admin/reports/dropouts", f.params);
  const handleExport = (fmt) => fmt === "refresh" ? reload() : download(fmt);
  return (
    <div data-testid="dropout-tab">
      <ReportFilters {...f} onExport={handleExport} isLoading={loading} />
      {loading || !data ? <Spinner /> : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
            <div className="rounded-lg border bg-card p-4">
              <p className="text-xs uppercase text-muted-foreground">Total drop-outs</p>
              <p className="text-xl font-bold mt-1" data-testid="dropout-total">{data.count}</p>
            </div>
            <div className="rounded-lg border bg-card p-4">
              <p className="text-xs uppercase text-muted-foreground">Active students</p>
              <p className="text-xl font-bold mt-1">{data.active_students}</p>
            </div>
            <div className="rounded-lg border bg-card p-4">
              <p className="text-xs uppercase text-muted-foreground">Churn rate</p>
              <p className="text-xl font-bold mt-1 text-amber-700">{data.churn_rate_pct}%</p>
            </div>
          </div>
          <div className="rounded-lg border bg-card p-5 mb-4">
            <h3 className="text-sm font-semibold mb-3">Drop-out reasons</h3>
            <div className="flex flex-wrap gap-3">
              {Object.entries(data.by_category).map(([k, v]) => (
                <div key={k} className="rounded-md border px-3 py-2 text-sm" data-testid={`by-cat-${k}`}>
                  <span className="text-muted-foreground capitalize">{k.replace("_", " ")}: </span>
                  <span className="font-semibold">{v}</span>
                </div>
              ))}
            </div>
          </div>
          <DataTable rows={data.rows} loading={false} columns={[
            { key: "date", label: "Date" }, { key: "student", label: "Student" },
            { key: "category", label: "Category",
              render: (v) => <span className="text-xs capitalize">{(v || "other").replace("_", " ")}</span> },
            { key: "reason", label: "Notes" },
          ]} testId="dropout-table" />
        </>
      )}
    </div>
  );
}

// ---------------------- 7) Payroll ----------------------
function PayrollTab() {
  const [month, setMonth] = useState(MONTH_NOW);
  const { data, loading, reload, download } = useReport("/admin/reports/payroll", { month });
  return (
    <div data-testid="payroll-tab">
      <div className="flex flex-wrap items-end gap-3 mb-5 rounded-lg border bg-card p-4">
        <div className="space-y-1">
          <Label className="text-xs">Month</Label>
          <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)}
                 className="w-40" data-testid="filter-month" />
        </div>
        <div className="flex-1"></div>
        <Button variant="outline" size="sm" onClick={() => download("csv")} disabled={loading} data-testid="export-csv">
          <FileDown className="h-4 w-4 mr-1" />CSV
        </Button>
        <Button variant="outline" size="sm" onClick={() => download("pdf")} disabled={loading} data-testid="export-pdf">
          <FileText className="h-4 w-4 mr-1" />PDF
        </Button>
        <Button variant="ghost" size="sm" onClick={reload} disabled={loading} data-testid="refresh-report">
          <RefreshCcw className="h-4 w-4" />
        </Button>
      </div>
      {loading || !data ? <Spinner /> : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <div className="rounded-lg border bg-card p-4">
              <p className="text-xs uppercase text-muted-foreground">Teachers</p>
              <p className="text-xl font-bold mt-1" data-testid="payroll-count">{data.count}</p>
            </div>
            <div className="rounded-lg border bg-card p-4">
              <p className="text-xs uppercase text-muted-foreground">Gross commission</p>
              <p className="text-xl font-bold mt-1">{fmtRM(data.totals.gross_commission)}</p>
            </div>
            <div className="rounded-lg border bg-card p-4">
              <p className="text-xs uppercase text-muted-foreground">Net payable</p>
              <p className="text-xl font-bold mt-1">{fmtRM(data.totals.total_net_payable)}</p>
            </div>
            <div className="rounded-lg border bg-card p-4 bg-secondary/5">
              <p className="text-xs uppercase text-secondary">Total company cost</p>
              <p className="text-xl font-bold mt-1 text-secondary">{fmtRM(data.totals.total_company_cost)}</p>
            </div>
          </div>
          <DataTable rows={data.rows} loading={false} columns={[
            { key: "teacher", label: "Teacher" },
            { key: "gross_commission", label: "Gross", align: "right", render: (v) => fmtRM(v) },
            { key: "adjustment", label: "Adjustment", align: "right", render: (v) => fmtRM(v) },
            { key: "employee_deductions", label: "Deductions", align: "right", render: (v) => fmtRM(v) },
            { key: "net_payable", label: "Net Pay", align: "right", render: (v) => fmtRM(v) },
            { key: "employer_contribution", label: "Employer", align: "right", render: (v) => fmtRM(v) },
            { key: "company_cost", label: "Co. Cost", align: "right", render: (v) => fmtRM(v) },
            { key: "status", label: "Status", render: (v) => <StatusBadge status={v} /> },
          ]} testId="payroll-table" />
        </>
      )}
    </div>
  );
}

// ---------------------- Mini stat card + Data table ----------------------
function StatCard({ label, value, accent, testId }) {
  const ring = accent === "emerald" ? "border-emerald-200 bg-emerald-50/50"
              : accent === "rose" ? "border-rose-200 bg-rose-50/50"
              : accent === "amber" ? "border-amber-200 bg-amber-50/50"
              : "";
  return (
    <div className={`rounded-lg border bg-card p-4 ${ring}`} data-testid={testId}>
      <p className="text-xs uppercase text-muted-foreground">{label}</p>
      <p className="text-xl font-bold mt-1">{value}</p>
    </div>
  );
}

function DataTable({ rows, columns, testId }) {
  if (!rows || rows.length === 0) return <EmptyState text="No data for this period." />;
  return (
    <div className="rounded-lg border overflow-x-auto" data-testid={testId}>
      <table className="min-w-full text-sm">
        <thead className="bg-muted/40">
          <tr>
            {columns.map((c) => (
              <th key={c.key} className={`px-3 py-2 text-xs font-bold uppercase tracking-wide ${c.align === "right" ? "text-right" : "text-left"}`}>
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, idx) => (
            <tr key={idx} className="border-t hover:bg-muted/20">
              {columns.map((c) => (
                <td key={c.key} className={`px-3 py-2 ${c.align === "right" ? "text-right" : ""}`}>
                  {c.render ? c.render(r[c.key], r) : (r[c.key] ?? "—")}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------- 8) Activity Log ----------------------
function ActivityLogTab() {
  const f = useFilterState();
  const { data, loading, reload, download } = useReport("/admin/reports/activity-log", f.params);
  const handleExport = (fmt) => fmt === "refresh" ? reload() : download(fmt);
  return (
    <div data-testid="activity-tab">
      <ReportFilters {...f} onExport={handleExport} isLoading={loading} />
      {loading || !data ? <Spinner /> : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
            <div className="rounded-lg border bg-card p-4">
              <p className="text-xs uppercase text-muted-foreground">Total events</p>
              <p className="text-xl font-bold mt-1" data-testid="activity-total">{data.count}</p>
            </div>
            <div className="rounded-lg border bg-card p-4 md:col-span-2">
              <p className="text-xs uppercase text-muted-foreground mb-1">By action</p>
              <div className="flex flex-wrap gap-x-3 gap-y-1">
                {Object.entries(data.by_action).map(([k, v]) => (
                  <span key={k} className="text-sm">
                    <span className="text-muted-foreground">{k}:</span>{" "}
                    <span className="font-semibold">{v}</span>
                  </span>
                ))}
              </div>
            </div>
          </div>
          <DataTable rows={data.rows} loading={false} columns={[
            { key: "when", label: "When" },
            { key: "actor", label: "Who" },
            { key: "action", label: "Action" },
            { key: "table", label: "Entity" },
            { key: "record_id", label: "Record" },
          ]} testId="activity-table" />
        </>
      )}
    </div>
  );
}

// ---------------------- Main page ----------------------
export default function ReportsPage() {
  return (
    <div data-testid="reports-page">
      <PageHeader title="Reports" description="Financial and operational reports — all exportable to CSV / PDF." />
      <Tabs defaultValue="summary" className="w-full">
        <TabsList className="grid w-full grid-cols-2 md:grid-cols-4 lg:grid-cols-8 mb-4">
          <TabsTrigger value="summary" data-testid="tab-summary">Summary</TabsTrigger>
          <TabsTrigger value="collection" data-testid="tab-collection">Collection</TabsTrigger>
          <TabsTrigger value="sst" data-testid="tab-sst">SST</TabsTrigger>
          <TabsTrigger value="deposit" data-testid="tab-deposit">Deposit</TabsTrigger>
          <TabsTrigger value="movement" data-testid="tab-movement">Movement</TabsTrigger>
          <TabsTrigger value="dropout" data-testid="tab-dropout">Drop-out</TabsTrigger>
          <TabsTrigger value="payroll" data-testid="tab-payroll">Payroll</TabsTrigger>
          <TabsTrigger value="activity" data-testid="tab-activity">Activity Log</TabsTrigger>
        </TabsList>
        <TabsContent value="summary"><SummaryTab /></TabsContent>
        <TabsContent value="collection"><CollectionTab /></TabsContent>
        <TabsContent value="sst"><SSTTab /></TabsContent>
        <TabsContent value="deposit"><DepositTab /></TabsContent>
        <TabsContent value="movement"><MovementTab /></TabsContent>
        <TabsContent value="dropout"><DropoutTab /></TabsContent>
        <TabsContent value="payroll"><PayrollTab /></TabsContent>
        <TabsContent value="activity"><ActivityLogTab /></TabsContent>
      </Tabs>
    </div>
  );
}
